/* eslint-disable no-console */
import type { TokenAudience, TokenUpdate } from '@lseg-ciam/auth/token';
import {
  claimRealTimeData,
  connectToken$,
  dataToken$,
  getConnectToken,
  getDataToken,
  getRestrictedToken,
  getThirdpartyToken,
  restrictedToken$,
  thirdpartyToken$,
} from '@lseg-ciam/auth/token';
import { isHeimdallEnabled } from '@lseg-ciam/auth/utils';
// api.ws stream/getter come from the HCL wrapper so CDN relay fires on refresh
import { apiToken$, getApiToken, logOut } from '@heimdall/client';
import { notifyRefresh$ } from '@lseg-ciam/auth/token';
import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Observable, Subscription } from 'rxjs';
import { rigSharedStyles } from './heimdall-rig-styles.js';

const audiences: TokenAudience[] = [
  'api.ws',
  'data.ws',
  'restricted.ws',
  'thirdparty.ws',
  'connect.ws',
];

const streamByAudience: Record<TokenAudience, Observable<TokenUpdate>> = {
  ['api.ws']: apiToken$,
  ['data.ws']: dataToken$,
  ['restricted.ws']: restrictedToken$,
  ['thirdparty.ws']: thirdpartyToken$,
  ['connect.ws']: connectToken$,
};

const singleTokenByAudience: Record<TokenAudience, () => Promise<TokenUpdate>> =
  {
    ['api.ws']: getApiToken,
    ['data.ws']: getDataToken,
    ['restricted.ws']: getRestrictedToken,
    ['thirdparty.ws']: getThirdpartyToken,
    ['connect.ws']: getConnectToken,
  };

@customElement('heimdall-rig-audience')
export class HeimdallRigAudience extends LitElement {
  _debugInstanceId = Math.floor(Math.random() * 0xfff)
    .toString(32)
    .toUpperCase();

  @state()
  _subscriptionByAudience: Partial<Record<TokenAudience, Subscription>> = {};

  @state()
  _updatesByAudience: Partial<Record<TokenAudience, TokenUpdate[]>> = {};

  @state()
  _updateByAudience: Partial<Record<TokenAudience, TokenUpdate>> = {};

  @state()
  _singlePendingByAudience: Partial<Record<TokenAudience, boolean>> = {};

  @state()
  _error?: unknown;

  onIsHeimdallEnabled() {
    alert(
      isHeimdallEnabled()
        ? `Yes, Heimdall is enabled — user successfully logged in and hasn't opted out.`
        : `No, Heimdall is not enabled — user has either not logged in or opted out.`
    );
  }

  onGetSingleTokenRequest(audience: TokenAudience) {
    return async () => {
      this._singlePendingByAudience = {
        ...this._singlePendingByAudience,
        [audience]: true,
      };
      const update = await singleTokenByAudience[audience]();
      delete this._singlePendingByAudience[audience];
      this._singlePendingByAudience = {
        ...this._singlePendingByAudience,
      };
      this._updateByAudience = {
        ...this._updateByAudience,
        [audience]: update,
      };
    };
  }

  onSubscriptionRequestChanged(audience: TokenAudience) {
    return (event: Event) => {
      const target = event.target as HTMLInputElement;
      const { checked } = target;

      if (checked) {
        this._subscriptionByAudience = {
          ...this._subscriptionByAudience,
          [audience]: streamByAudience[audience].subscribe({
            next: (update) => {
              const currentUpdates = this._updatesByAudience[audience] || [];
              this._updatesByAudience[audience] = [...currentUpdates, update];
              this._updatesByAudience = { ...this._updatesByAudience };
            },
            complete: () => {
              this.removeSubscription(audience);
            },
          }),
        };
      } else {
        this.removeSubscription(audience);
      }
    };
  }

  removeSubscription(audience: TokenAudience) {
    this._subscriptionByAudience[audience]?.unsubscribe();
    delete this._subscriptionByAudience[audience];
    this._updatesByAudience[audience] = [];
    this._updatesByAudience = { ...this._updatesByAudience };
    this._subscriptionByAudience = { ...this._subscriptionByAudience };
  }

  render() {
    if (this._error)
      return html`<div class="app">
        <h1>💀</h1>
        <pre>${this._error}</pre>
      </div>`;
    return html`
      <div class="app">
        <h2>${this._debugInstanceId}</h2>
        <ul class="operation-rig">
          ${audiences.map((audience) => {
            const active =
              !!this._subscriptionByAudience[audience] &&
              !this._subscriptionByAudience[audience]?.closed; // perhaps redundant
            return html`
              <li>
                <label class="${active ? 'active' : ''}">
                  <input
                    type="checkbox"
                    .checked=${active}
                    @change=${this.onSubscriptionRequestChanged(audience)}
                  />
                  Subscribe to token updates for ${audience}
                </label>
                <p>
                  ${(this._updatesByAudience[audience] || []).map((update) => {
                    return createUpdate(audience, update);
                  })}
                </p>
                <hr />
                <label
                  class=${this._singlePendingByAudience[audience]
                    ? 'pending'
                    : ''}
                >
                  <button @click=${this.onGetSingleTokenRequest(audience)}>
                    Get single token update
                  </button>
                  for ${audience}</label
                >
                <p>
                  ${this._updateByAudience[audience] &&
                  createUpdate(audience, this._updateByAudience[audience])}
                </p>
              </li>
            `;
          })}
          <li>
            <p>
              <button
                @click=${() => {
                  notifyRefresh$.next(`PN.${Date.now()}`);
                }}
              >
                Notify refresh (REF ${this._debugInstanceId})
              </button>
            </p>
            <p>
              <button @click=${claimRealTimeData}>
                Reclaim DATA real-time
              </button>
            </p>
            <p>
              <button @click=${this.onIsHeimdallEnabled}>
                Is Heimdall enabled?
              </button>
            </p>
            <p>
              <button @click=${logOut}>Log out</button>
            </p>
          </li>
        </ul>
      </div>
    `;
  }

  static get styles() {
    return rigSharedStyles;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-rig-audience': HeimdallRigAudience;
  }
}

function createUpdate(audience: TokenAudience, tokenUpdate?: TokenUpdate) {
  if (!tokenUpdate) return;

  if ('isRealTime' in tokenUpdate) {
    const { isRealTime } = tokenUpdate;

    return createTokenPill(
      `T ∴ ${audience}·${tokenUpdate.token.slice(-4)} ${
        isRealTime ? `RT:📲` : `D:📞`
      }`,
      isRealTime ? 'realtime' : 'delayed'
    );
  }

  return createTokenPill(`T ∴ ${audience}·${tokenUpdate.token.slice(-4)} 🎫`);
}

function createTokenPill(token: string, extraClass = '') {
  return html`<span class="token ${extraClass}">${token}</span> `;
}
